import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Button, Modal } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import server from "../../../server";
import TrolleyModal from "./TrolleyModal";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const headCells = [
  {
    id: "qrCode",
    numeric: false,
    disablePadding: true,
    label: "QrCode",
  },
  {
    id: "isEngaged",
    numeric: false,
    disablePadding: true,
    label: "isEngaged",
  },
  {
    id: "physicalWeight",
    numeric: true,
    disablePadding: false,
    label: "physicalWeight",
  },
  {
    id: "virtualCartWeight",
    numeric: true,
    disablePadding: false,
    label: "virtualCartWeight",
  },
  {
    id: "alertLight",
    numeric: true,
    disablePadding: false,
    label: "alertLight",
  },
  {
    id: "Actions",
    numeric: true,
    disablePadding: false,
    label: "Actions",
  },
  //   {
  //     id: "Status",
  //     numeric: true,
  //     disablePadding: false,
  //     label: "Status",
  //   },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead style={{ borderTop: "1px solid #E1E2E9" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "left" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const handlesearch = async (e) => {
    if (e.target.value != "") {
      const filteredRows = await props.oriorderData.filter((row) => {
        return row?.name.toLowerCase()?.includes(e.target.value.toLowerCase());
      });

      props.setTrolleys(filteredRows);
    } else {
      props.setTrolleys(props.oriorderData);
    }
  };
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Trolleys
        </Typography>
      </div>
    </Toolbar>
  );
}

const TrolleyTable = (props) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [trolleys, setTrolleys] = useState(props.trolleys);
  const [trolleyInformation, setTrolleyInformation] = useState({
    qrCode: "",
    isEngaged: false,
    physicalWeight: "1",
    virtualCartWeight: "0",
    _id: "",
  });

  const [updateFields, setUpdateFields] = useState();
  const [open, setOpen] = useState(false);
  const [deleteTrolleyId, setDeleteTrolleyId] = useState("");
  const [logoutModal, setLogooutModal] = useState(false);
  const handleOpenLogoutModal = (trolleyId) => {
    setDeleteTrolleyId(trolleyId);
    setLogooutModal(true);
  };
  const handleCloseLogoutModal = () => setLogooutModal(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const deleteTrolley = async (trolleyId) => {
    try {
      const result = await axios.delete(
        `${server}/trolley/delete/${trolleyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      setTrolleys((prevCustomers) =>
        prevCustomers.filter((customer) => customer._id !== trolleyId),
      );
      toast.success("Trolley Deleted Successfully!");
    } catch (error) {
      console.log(error);
      toast.error("Failed to Delete!");
    }
  };

  const updateTrolley = async () => {
    try {
      const result = await axios.patch(
        `${server}/trolley/update/${trolleyInformation._id}`,

        trolleyInformation,

        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );
      setTrolleys((prevCustomers) =>
        prevCustomers?.map((customer) =>
          customer._id === trolleyInformation._id ? result.data : customer,
        ),
      );
      toast.success("Trolley Updated");
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await updateTrolley();
    handleClose();
  };

  const handleStatus = async (employeeId, value) => {
    try {
      const result = await axios.patch(
        `${server}/employees/update/${employeeId}`,
        {
          isActive: value,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );
      toast.success("Status Updated");
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (trolleyId) => {
    await deleteTrolley(trolleyId);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.rows.length) : 0;

  useEffect(() => {
    setTrolleys(props.trolleys);
  }, [props.trolleys]);

  const tableRef = useRef(null);

  return (
    <div className="order-table-main">
      <Box sx={{ width: "100%" }}>
        <Modal
          open={logoutModal}
          onClose={handleCloseLogoutModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Are you Sure, You want to Delete?
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                // className="create-new-post-button"
                style={{ margin: "10px 20px" }}
                onClick={() => {
                  handleDelete(deleteTrolleyId);
                  setDeleteTrolleyId("");
                  handleCloseLogoutModal();
                }}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                color="primary"
                // className="create-new-post-button"
                style={{ margin: "10px 20px" }}
                onClick={handleCloseLogoutModal}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TrolleyModal
            open={open}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            trolleyInformation={trolleyInformation}
            setTrolleyInformation={setTrolleyInformation}
            setUpdateFields={setUpdateFields}
            isEdit={true}
          />
          <EnhancedTableToolbar
            trolleys={trolleys}
            setTrolleys={setTrolleys}
            oriorderData={props.trolleys}
            tableRef={tableRef}
          />
          <TableContainer style={{ padding: "0px 20px" }}>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
              ref={tableRef}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                oriorderData={props.trolleys}
                onRequestSort={handleRequestSort}
                rowCount={trolleys?.length}
              />
              <TableBody>
                {(rowsPerPage > 0
                  ? trolleys?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : trolleys
                )?.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row._id}
                      sx={{ cursor: "pointer" }}
                      style={{ border: "1px solid transparent" }}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        style={{ border: "1px solid transparent" }}
                      >
                        {row.qrCode ? <>{row.qrCode}</> : <>No Qr</>}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{ border: "1px solid transparent" }}
                      >
                        {row?.isEngaged ? "True" : "False"}
                      </TableCell>

                      <TableCell
                        align="left"
                        style={{ border: "1px solid transparent" }}
                      >
                        {row?.physicalWeight}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{ border: "1px solid transparent" }}
                      >
                        {row?.virtualCartWeight}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{ border: "1px solid transparent" }}
                      >
                        {row?.alertLight == "nolight"
                          ? "No Light"
                          : row.alertLight}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{ border: "1px solid transparent" }}
                      >
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleOpenLogoutModal(row._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => {
                              handleOpen();
                              setTrolleyInformation(row);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      {/* <TableCell style={{ border: "1px solid transparent" }}>
                        <select
                          defaultValue={row?.isActive}
                          className="status-published"
                          onChange={(e) => {
                            e.preventDefault();
                            handleStatus(row._id, e.target.value);
                          }}
                        >
                          <option value="true">Active</option>
                          <option value="false">inActive</option>
                        </select>
                      </TableCell> */}
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 25]}
            component="div"
            count={trolleys?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default TrolleyTable;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  padding: 4,
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "#ffffff",
  overflowX: "scroll",
  scrollbarWidth: "none",
  "-ms-overflow-style": "none",
  border: "none",
  borderRadius: "12px",
  boxShadow: 24,
  backdropfilter: "blur(4px)",
};
